/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-sparse-arrays */
import { Suspense, useMemo, lazy } from "react";
import { useSelector } from "react-redux";
import isMobile from "ismobilejs";
import FadeInOnView from "../components/FadeInOnView";
import HeaderSlider from "../components/HeaderSlider/HeaderSlider";
import Container from "../components/Container/Container";

import { useTranslation } from "react-i18next";
import CookieConsent from "react-cookie-consent";
import Spacer from "../components/Spacer/Spacer";

import { getSafe } from "../utils";
import { spacing } from "../utils/spacing";
import P from "../components/P/P";
import H1 from "../components/H1/H1";
import Seo from "../Seo";
import { useFetchCities } from "../hooks/useFetchCities";
import { useFetchPlaces } from "../hooks/useFetchPlaces";
import { useFetchExperiences } from "../hooks/useFetchExperiences";
import { useTheme } from "styled-components";
import { shuffleArray } from "../helpers";
import { HERO_IMAGE_PATHS } from "../constants/cityImages";

// Lazy load heavy components
const DefaultSwiper = lazy(() =>
  import("../components/DefaultSwiper/DefaultSwiper")
);

const InstaWall = lazy(() => import("../components/InstaWall/InstaWall"));

const Newsletter = lazy(() => import("../components/Newsletter/Newsletter"));

const RegionSelector = lazy(() =>
  import("../components/RegionSelector/RegionSelector")
);

const shuffeledHeroImages = shuffleArray(HERO_IMAGE_PATHS);

function Home() {
  const { data: experiences = [] } = useFetchExperiences();
  const { data: cities } = useFetchCities();
  const { data: placesResult } = useFetchPlaces();

  const { local } = useSelector((state) => state.Local);
  let { t } = useTranslation();

  const theme = useTheme();

  const shuffledExp = useMemo(() => {
    if (!experiences || experiences.length === 0) return [];
    return shuffleArray(experiences);
  }, [experiences]);

  const shuffledPlaces = useMemo(() => {
    const places = placesResult?.places ?? placesResult?.all ?? [];
    if (!places || places.length === 0) return [];
    return shuffleArray(places);
  }, [placesResult]);

  return (
    <div style={{ overflow: "hidden" }}>
      <Seo
        title={t("seoHomeTitle")}
        description={t("seoPlanningDescription")}
        local={local}
      />
      <CookieConsent
        style={{
          background: theme.yellowish,
          fontFamily: theme.fonts.ivy,
          color: theme.green,
          fontSize: "28px",
        }}
        buttonStyle={{
          background: theme.green,
          fontFamily: theme.fonts.ivy,
          color: theme.white,
          fontSize: "28px",
        }}
        declineButtonStyle={{
          background: theme.lightblue,
          fontFamily: theme.fonts.ivy,
          color: theme.white,
          fontSize: "28px",
        }}
        enableDeclineButton
        ariaLabel="Cookie consent notice"
      >
        This website uses cookies to enhance the user experience.
      </CookieConsent>
      <section aria-labelledby="hero-title" role="banner">
        <FadeInOnView>
          <Container>
            {!isMobile().phone && <Spacer bottom="40px" />}
            <H1 id="hero-title" big bold notwide>
              {t("herotitle")}
            </H1>
            <P squeeze>{t("herodesc")}</P>

            <HeaderSlider
              img={shuffeledHeroImages.slice(0, 5).map((imagePath, index) => ({
                id: `hero-${index}`,
                src: imagePath,
              }))}
              aria-label="Featured destination images"
            />

            <Spacer
              bottom={
                isMobile().phone
                  ? spacing.section.mobile
                  : spacing.section.desktop
              }
            />
          </Container>
        </FadeInOnView>
      </section>
      <section aria-labelledby="experiences-title">
        <FadeInOnView>
          <Suspense
            fallback={
              <div
                style={{ height: "400px", background: "#f5f5f5" }}
                aria-label="Loading experiences..."
              />
            }
          >
            <DefaultSwiper
              slides={shuffledExp}
              getSafe={getSafe}
              exp={true}
              main={t("topExperience")}
              sub={t("topExperiencesub")}
              aria-labelledby="experiences-title"
            />
          </Suspense>
        </FadeInOnView>
      </section>
      <Spacer bottom="10rem" />
      <FadeInOnView>
        <Suspense
          fallback={<div style={{ height: "600px", background: "#f5f5f5" }} />}
        >
          <RegionSelector
            cities={cities}
            local={local}
            title={t("theRegion")}
            subtitle={t("theRegionDesc")}
          />
        </Suspense>
      </FadeInOnView>
      <Spacer bottom="10rem" />
      <FadeInOnView>
        <Suspense
          fallback={<div style={{ height: "400px", background: "#f5f5f5" }} />}
        >
          <DefaultSwiper
            slides={shuffledPlaces}
            getSafe={getSafe}
            todo={true}
            main={t("todo")}
            sub={t("tododesc")}
          />
        </Suspense>
      </FadeInOnView>
      <Spacer bottom="10rem" />
      <FadeInOnView>
        <Suspense
          fallback={<div style={{ height: "400px", background: "#f5f5f5" }} />}
        >
          <DefaultSwiper
            getSafe={getSafe}
            slides={shuffledPlaces}
            event={true}
            filter={["evenements"]}
            main={t("events")}
            sub={t("eventssub")}
          />
        </Suspense>
      </FadeInOnView>
      <Spacer bottom="10rem" />
      <FadeInOnView>
        <Suspense
          fallback={<div style={{ height: "300px", background: "#f5f5f5" }} />}
        >
          <Newsletter />
        </Suspense>
      </FadeInOnView>
      <Spacer bottom="10rem" />
      <FadeInOnView>
        <Container>
          <H1>{t("InstaHashtag")}</H1>
          <Suspense
            fallback={
              <div style={{ height: "400px", background: "#f5f5f5" }} />
            }
          >
            <InstaWall />
          </Suspense>
        </Container>
      </FadeInOnView>
    </div>
  );
}

export default Home;
